import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import api from '../../api';
import { useAppSelector } from '../../hooks/useApp';
import { AuthState } from './authSlice';

export interface CheckState {
  status: 'idle' | 'loading' | 'failed' | 'success';
  hotels: HotelsState;
  flights: FlightsState;
  totalPointsNeeded: number;
  message?: string;
  predefinedValues?: PredefinedValues;
}

interface HotelsState {
  guests?: number;
  nights?: number;
  destination?: string;
  successInfo?: SuccessInfo[];
  hotel?: WithInfo;
  totalPointsNeeded?: number;
}

interface FlightsState {
  passengers?: number;
  class?: string;
  destination?: string;
  departure?: string;
  successInfo?: SuccessInfo[];
  airline?: WithInfo;
  totalPointsNeeded?: number;
}

interface PredefinedValues {
  people?: number | null;
  nights?: number | null;
  class?: string | null;
  destination?: string | null;
  defined: boolean;
}

interface SuccessInfo {
  program: string;
  points: number;
  convertTo?: string;
  convertedPoints?: number;
}

interface WithInfo {
  name: string;
  id: string;
}

const initialState: CheckState = {
  status: 'idle',
  hotels: {
    guests: 1,
    nights: 2,
    destination: '',
    successInfo: [],
    hotel: {
      name: '',
      id: ''
    }
  },
  flights: {
    passengers: 1,
    class: 'Economy',
    departure: '',
    destination: '',
    successInfo: [],
    airline: {
      name: '',
      id: ''
    }
  },
  totalPointsNeeded: 0
};

export const checkFlights = createAsyncThunk(
  'checks/flights',
  async (_, { getState }) => {
    const rootState: any = getState();
    const checkState: CheckState = rootState.checks;
    const authState: AuthState = rootState.auth;
    const {clientId, source} = authState;

    const response = await api.post(
      '/goals/check/flightGoal',
      {
        userId: rootState.auth.userId,
        numPeople: checkState.flights.passengers,
        city: checkState.flights.destination?.split(',')[0].trim(),
        country: checkState.flights.destination?.split(',')[1].trim(),
        ticketClass: checkState.flights.class
      },
      {
        headers: {
          Authorization: `Bearer ${rootState.auth.token}`
        }
      }
    );

    // analytics
    analytics.track('Flight Search Query', {
      userId: rootState.auth.userId,
      numPeople: checkState.flights.passengers,
      departure: checkState.flights.departure,
      destination: checkState.flights.destination,
      ticketClass: checkState.flights.class,
      location: window.location.href,
      clientId, 
      source
    });
    return response.data;
  }
);

export const checkHotels = createAsyncThunk(
  'checks/hotels',
  async (_, { getState }) => {
    const rootState: any = getState();
    const checkState: CheckState = rootState.checks;
    const authState: AuthState = rootState.auth;
    const {clientId, source} = authState;
    const response = await api.post(
      '/goals/check/hotelGoal',
      {
        userId: rootState.auth.userId,
        numPeople: checkState.hotels.guests,
        numDays: checkState.hotels.nights,
        city: checkState.hotels.destination?.split(',')[0].trim(),
        country: checkState.hotels.destination?.split(',')[1].trim()
      },
      {
        headers: {
          Authorization: `Bearer ${rootState.auth.token}`
        }
      }
    );

    // analytics
    analytics.track('Hotels Search Query', {
      userId: rootState.auth.userId,
      numPeople: checkState.hotels.guests,
      numDays: checkState.hotels.nights,
      city: checkState.hotels.destination?.split(',')[0].trim(),
      country: checkState.hotels.destination?.split(',')[1].trim(),
      location: window.location.href,
      clientId, 
      source
    });
    return response.data;
  }
);

export const checksSlice = createSlice({
  name: 'checks',
  initialState,
  reducers: {
    readyCheck: (state: CheckState) => {
      state.status = 'idle';
    },
    resetMessage: (state: CheckState) => {
      state.status = 'idle';
      state.message = '';
    },
    updateHotels: (state: CheckState, action: PayloadAction<HotelsState>) => {
      state.hotels = { ...state.hotels, ...action.payload };
    },
    updateFlights: (state: CheckState, action: PayloadAction<FlightsState>) => {
      state.flights = { ...state.flights, ...action.payload };
    },
    setPredefinedValues: (
      state: CheckState,
      action: PayloadAction<PredefinedValues>
    ) => {
      if (state.predefinedValues?.defined) {
        return;
      }

      state.predefinedValues = action.payload;

      state.hotels.guests = action.payload.people
        ? action.payload.people
        : state.hotels.guests;
      state.hotels.nights = action.payload.nights
        ? action.payload.nights
        : state.hotels.nights;
      state.hotels.destination = action.payload.destination
        ? action.payload.destination
        : state.hotels.destination;

      state.flights.passengers = action.payload.people
        ? action.payload.people
        : state.flights.passengers;
      state.flights.class = action.payload.class
        ? action.payload.class
        : state.flights.class;
      state.flights.destination = action.payload.destination
        ? action.payload.destination
        : state.flights.destination;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkHotels.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(checkHotels.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.status = 'success';
          state.hotels.successInfo = action.payload.data.info;
          state.hotels.hotel = action.payload.data.hotel;

          // analytics
          analytics.track('Hotels Search Results - Passed', {
            status: 'success',
            destination: state.hotels.destination,
            hotels_successInfo: { ...action.payload.data.info },
            hotels_hotel: action.payload.data.hotel,
            location: window.location.href
          });
        } else {
          state.status = 'failed';
          if (action.payload.hotel)
            state.totalPointsNeeded = action.payload.hotel.pointsNeeded;
          else
            state.message = `No available hotels for ${state.hotels.destination}.`;
          // analytics
          analytics.track('Hotels Search Results - Failed', {
            status: 'failed',
            message: action.payload.message,
            destination: state.hotels.destination,
            totalPointsNeeded: action.payload?.hotel?.pointsNeeded,
            location: window.location.href
          });
        }
      })
      .addCase(checkHotels.rejected, (state, action) => {
        state.status = 'failed';
        console.log(action.payload);
      })
      .addCase(checkFlights.pending, (state, action) => {
        state.status = 'loading';
        state.message = '';
      })
      .addCase(checkFlights.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.status = 'success';
          state.flights.successInfo = action.payload.data.info;
          state.flights.airline = action.payload.data.airline;

          // analytics
          analytics.track('Flights Search Results - Passed', {
            status: 'success',
            depature: state.flights.departure,
            destination: state.flights.destination,
            flights_successInfo: { ...action.payload.data.info },
            flights_hotel: action.payload.data.hotel,
            location: window.location.href
          });
        } else {
          state.status = 'failed';
          if (action.payload.airline)
            state.totalPointsNeeded = action.payload.airline.pointsNeeded;
          else
            state.message = `No available flights for ${state.flights.destination}.`;

          // analytics
          analytics.track('Flights Search Results - Failed', {
            status: 'failed',
            depature: state.flights.departure,
            destination: state.flights.destination,
            message: action.payload.message,
            totalPointsNeeded: action.payload?.airline?.pointsNeeded,
            location: window.location.href
          });
        }
      })
      .addCase(checkFlights.rejected, (state, action) => {
        state.status = 'failed';
        console.log(action.payload);
      });
  }
});

export const {
  readyCheck,
  resetMessage,
  updateHotels,
  updateFlights,
  setPredefinedValues
} = checksSlice.actions;

export default checksSlice.reducer;
